import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, ArrowRight, Check, RefreshCcw, Sparkles, Users, DollarSign, Flame } from "lucide-react";
import { toast } from "sonner";

// Occasion → ranked case ids (first match wins among in-stock cases)
const OCCASION_PICKS = {
  halloween:  ["seventh_candle", "crimson_ball", "cosplay_crime", "blackthorn"],
  valentines: ["who_shot_cupid", "till_death", "dinner_for_four"],
  birthday:   ["last_toast", "blackthorn", "cosplay_crime"],
  friends:    ["last_toast", "cosplay_crime", "dinner_for_four", "seen_by_6"],
  team:       ["countdown_killer", "last_toast"],
  holiday:    ["secret_santa", "seventh_candle"],
  wedding:    ["till_death"],
  nye:        ["countdown_killer", "last_toast"],
};

// Intensity → ranked case ids. Horror trio for terrifying, cozy for safe plays.
const INTENSITY_PICKS = {
  cozy:       ["dinner_for_four", "secret_santa", "last_toast", "who_shot_cupid"],
  creepy:     ["blackthorn", "seen_by_6", "cosplay_crime", "last_toast"],
  terrifying: ["seventh_candle", "crimson_ball", "blackthorn"],
};

const Plan = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [packsByCase, setPacksByCase] = useState({});
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    occasion: "", group_size: 6, intensity: "creepy", budget: 50,
  });

  useEffect(() => {
    api.get("/cases").then(async (r) => {
      // The /cases endpoint shape varies — tolerate either {cases:[...]} or [...]
      const list = Array.isArray(r.data) ? r.data : (r.data.cases || []);
      setCases(list.filter((c) => !c.coming_soon));
      // Fetch pack manifests for cases that have them
      const horrorIds = list.filter((c) => c.id === "seventh_candle" || c.id === "crimson_ball");
      for (const c of horrorIds) {
        try {
          const p = await api.get(`/packs/for-case/${c.id}`);
          if (p.data.available) setPacksByCase((s) => ({ ...s, [c.id]: p.data }));
        } catch { /* ignore */ }
      }
    }).catch(() => toast.error("Could not load cases."));
  }, []);

  const recommendation = useMemo(() => {
    if (!cases.length) return null;
    if (!answers.occasion) return null;

    // Build a ranked list — union of occasion + intensity, dedupe
    const occList = OCCASION_PICKS[answers.occasion] || [];
    const intList = INTENSITY_PICKS[answers.intensity] || [];
    const ranked = [...new Set([...occList, ...intList])];

    const inStock = (id) => cases.find((c) => c.id === id);
    let pickedCase = null;
    for (const id of ranked) {
      const c = inStock(id);
      if (!c) continue;
      // Group size filter — case must support this size
      if (c.group_sizes?.length && !c.group_sizes.includes(answers.group_size) &&
          !c.group_sizes.some((s) => Math.abs(s - answers.group_size) <= 1)) continue;
      pickedCase = c; break;
    }
    if (!pickedCase) pickedCase = cases[0];

    // Add-ons: if we have packs for this case AND intensity is creepy/terrifying, suggest bundle
    const packs = packsByCase[pickedCase.id];
    const want_bundle = packs && ["creepy", "terrifying"].includes(answers.intensity);
    const addOnIds = want_bundle ? [packs.bundle.id] : [];
    const addOnCents = want_bundle ? packs.bundle.price_cents : 0;

    const case_cents = pickedCase.price_cents || 3500;
    const total = case_cents + addOnCents;
    // Why-this-fits
    const whyBits = [];
    const occLabel = { halloween: "a Halloween night", valentines: "a Valentine's night", birthday: "a birthday", friends: "a friends' night", team: "a team event", holiday: "a holiday party", wedding: "a wedding", nye: "New Year's Eve" }[answers.occasion] || "the night";
    const intLabel = { cozy: "keeps it cozy", creepy: "lands squarely in the creepy zone", terrifying: "goes for terrifying" }[answers.intensity];
    whyBits.push(`Written for ${answers.group_size} players and ${occLabel}, it ${intLabel}.`);
    if (want_bundle) whyBits.push(`The Complete Night Pack adds invitations, costume cards, and decor — the fastest way to turn the room.`);

    return {
      case: pickedCase,
      add_on_ids: addOnIds,
      add_on_cents: addOnCents,
      case_cents,
      total_cents: total,
      why: whyBits.join(" "),
    };
  }, [cases, packsByCase, answers]);

  const goBuy = () => {
    if (!recommendation) return;
    const params = new URLSearchParams({ case: recommendation.case.id });
    // Prefill the pack ids as a query string — Checkout.jsx will read it
    if (recommendation.add_on_ids.length) {
      params.set("packs", recommendation.add_on_ids.join(","));
    }
    api.post("/events", { event: "plan_purchase_clicked", props: { case_id: recommendation.case.id, bundle: recommendation.add_on_ids.length > 0 } }).catch(() => {});
    navigate(`/checkout?${params.toString()}`);
  };

  const total = cases.length;
  if (!total) return <div className="grain min-h-screen flex items-center justify-center text-[#a39797]"><Loader2 className="animate-spin" /></div>;

  const u = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const canAdvance = step === 0 ? !!answers.occasion
    : step === 1 ? !!answers.group_size
    : step === 2 ? !!answers.intensity
    : true;

  return (
    <div className="grain min-h-screen" data-testid="plan-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">plan my night</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-6">In four questions, your perfect case.</h1>

        {/* Step dots */}
        <div className="flex items-center gap-2 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div key={i}
              className={`h-1 flex-1 rounded ${i <= step ? "bg-[#8b0000]" : "bg-[#2d2424]"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="step-occasion">
            <h2 className="font-display text-2xl mb-3"><Sparkles size={18} className="inline mr-2 text-[#d4af37]" />What's the occasion?</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { id: "halloween", label: "Halloween" },
                { id: "valentines", label: "Valentine's" },
                { id: "birthday", label: "A birthday" },
                { id: "friends", label: "Friends' night" },
                { id: "nye", label: "New Year's Eve" },
                { id: "team", label: "A team event" },
                { id: "holiday", label: "A holiday" },
                { id: "wedding", label: "A wedding party" },
              ].map((o) => (
                <button key={o.id} onClick={() => u("occasion", o.id)}
                  data-testid={`plan-occ-${o.id}`}
                  className={`card-noir p-4 text-left transition-colors ${answers.occasion === o.id ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                  <div className="font-display text-lg">{o.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="step-size">
            <h2 className="font-display text-2xl mb-3"><Users size={18} className="inline mr-2 text-[#d4af37]" />How many players?</h2>
            <div className="grid grid-cols-5 gap-2 md:grid-cols-9">
              {[4,5,6,7,8,9,10,11,12].map((n) => (
                <button key={n} onClick={() => u("group_size", n)}
                  data-testid={`plan-size-${n}`}
                  className={`card-noir p-3 transition-colors ${answers.group_size === n ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                  <div className="font-display text-xl">{n}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="step-intensity">
            <h2 className="font-display text-2xl mb-3"><Flame size={18} className="inline mr-2 text-[#d4af37]" />How intense?</h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: "cozy", label: "Cozy", desc: "Low-stakes, laugh-first. Great with new groups." },
                { id: "creepy", label: "Creepy", desc: "A real mystery, a few jumps. The sweet spot." },
                { id: "terrifying", label: "Terrifying", desc: "Horror-leaning. Dim the lights." },
              ].map((o) => (
                <button key={o.id} onClick={() => u("intensity", o.id)}
                  data-testid={`plan-int-${o.id}`}
                  className={`card-noir p-4 text-left transition-colors ${answers.intensity === o.id ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                  <div className="font-display text-lg mb-1">{o.label}</div>
                  <div className="text-xs text-[#a39797]">{o.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && recommendation && (
          <div className="space-y-4" data-testid="step-recommendation">
            <h2 className="font-display text-2xl mb-3">Your case: <span className="text-[#d4af37]">{recommendation.case.title}</span></h2>
            <div className="card-noir p-5">
              <div className="text-xs tracking-widest text-[#a39797] mb-1">
                Case #{String(recommendation.case.number).padStart(3, "0")} · {recommendation.case.flavor_tags?.slice(0, 2).join(" · ")}
              </div>
              <div className="font-display text-xl mb-2">{recommendation.case.tagline}</div>
              <p className="text-[#c7bebe] text-sm leading-relaxed mb-3">{recommendation.case.description}</p>
              <p className="text-[#a39797] text-sm italic border-l-2 border-[#d4af37] pl-3">
                {recommendation.why}
              </p>
            </div>

            {recommendation.add_on_ids.length > 0 && packsByCase[recommendation.case.id] && (
              <div className="card-noir p-5" data-testid="plan-bundle">
                <div className="flex items-start gap-3">
                  <Check size={18} className="text-[#4ade80] mt-1" />
                  <div>
                    <div className="font-display text-lg">{packsByCase[recommendation.case.id].bundle.label}</div>
                    <div className="text-xs text-[#a39797]">
                      Invitations, costume cards, and decor — bundled for ${packsByCase[recommendation.case.id].bundle.price_cents / 100}, saving $5.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="card-noir p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-[#a39797] tracking-widest">YOUR CART</div>
                <div className="font-display text-3xl text-[#f4f0ec]" data-testid="plan-total">
                  ${(recommendation.total_cents / 100).toFixed(2)}
                </div>
                <div className="text-xs text-[#a39797] mt-1">
                  ${(recommendation.case_cents / 100).toFixed(0)} case{recommendation.add_on_cents > 0 ? ` + $${(recommendation.add_on_cents / 100).toFixed(0)} bundle` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setStep(0)} className="btn-ghost text-sm" data-testid="plan-restart">
                  <RefreshCcw size={14} /> start over
                </button>
                <button onClick={goBuy} className="btn-primary" data-testid="plan-goto-checkout">
                  <Skull size={16} /> continue to checkout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 3 && (
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0} className="btn-ghost text-sm disabled:opacity-30"
              data-testid="plan-prev">
              <ArrowLeft size={14} /> back
            </button>
            <button onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={!canAdvance}
              className="btn-primary disabled:opacity-50" data-testid="plan-next">
              next <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Plan;
